import { z } from "zod";
/** Top-frame HTTP ownership. Only plain request IDs cross Comlink; signals stay local. */
export function createAssistantModelRequests(
  options: { maxActive?: number; maxCanceled?: number } = {},
) {
  const maxActive = z
    .number()
    .int()
    .min(1)
    .max(32)
    .parse(options.maxActive ?? 8);
  const maxCanceled = z
    .number()
    .int()
    .min(1)
    .max(1024)
    .parse(options.maxCanceled ?? 256);
  const active = new Map<string, AbortController>(),
    pendingCanceled = new Set<string>(),
    settled = new Map<string, boolean>();
  const rememberSettled = (requestId: string, canceled: boolean) => {
    settled.delete(requestId);
    if (settled.size >= maxCanceled)
      settled.delete(settled.keys().next().value!);
    settled.set(requestId, canceled);
  };
  const id = (raw: string) => z.string().min(1).max(1500).parse(raw);
  let closed = false;
  return {
    async execute<T>(
      raw: string,
      start: (signal: AbortSignal) => Promise<T>,
    ): Promise<T> {
      const requestId = id(raw);
      if (closed || settled.get(requestId) === true)
        throw new Error("Assistant model request canceled or closed");
      // A pre-dispatch cancellation is pending until its invocation arrives.
      // Once consumed it is safe to rotate only the settled history.
      if (pendingCanceled.delete(requestId)) {
        rememberSettled(requestId, true);
        throw new Error("Assistant model request canceled before dispatch");
      }
      if (active.has(requestId) || active.size >= maxActive)
        throw new Error(
          "Assistant model request is duplicate or capacity is busy",
        );
      const controller = new AbortController();
      active.set(requestId, controller);
      try {
        return await Promise.resolve().then(() => {
          if (controller.signal.aborted)
            throw new Error("Assistant model request canceled before dispatch");
          return start(controller.signal);
        });
      } finally {
        active.delete(requestId);
        rememberSettled(requestId, controller.signal.aborted);
      }
    },
    cancel(raw: string) {
      const requestId = id(raw);
      const controller = active.get(requestId);
      if (controller) {
        controller.abort();
        return;
      }
      if (settled.has(requestId)) {
        settled.set(requestId, true);
        return;
      }
      // Never evict an unresolved pre-dispatch cancellation: that could let a
      // delayed invocation start. Only unresolved overflow closes this owner.
      if (
        pendingCanceled.size >= maxCanceled &&
        !pendingCanceled.has(requestId)
      )
        closed = true;
      else pendingCanceled.add(requestId);
    },
  };
}
